import { useState, useEffect } from 'react';
import { colors, ThemeMode } from '../styles/styles-data/colors';

/**
 * Custom hook for managing theme state across the application
 * Handles theme switching and persistence to localStorage
 */
export const useTheme = () => {
  // Initialize theme from localStorage or default to light mode
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('portfolio-theme') as ThemeMode;
      return savedTheme || 'light-mode';
    }
    return 'light-mode';
  });

  /**
   * Switch between light and dark themes
   */
  const toggleTheme = () => {
    const newTheme: ThemeMode = theme === 'light-mode' ? 'dark-mode' : 'light-mode';
    setThemeState(newTheme);
  };

  /**
   * Set a specific theme
   */
  const setTheme = (newTheme: ThemeMode) => {
    setThemeState(newTheme);
  };

  // Update colors system and persist theme when theme changes
  useEffect(() => {
    console.log('🎨 Theme switching to:', theme);
    
    // Update the colors system
    colors.setTheme(theme);
    
    // Inject CSS custom properties
    colors.injectCSSCustomProperties();
    
    // Add theme class to document for CSS targeting
    document.documentElement.setAttribute('data-theme', theme);
    
    // Persist to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('portfolio-theme', theme);
    }

    console.log('✅ Theme update complete');
  }, [theme]);

  return {
    theme,
    toggleTheme,
    setTheme,
    isLight: theme === 'light-mode',
    isDark: theme === 'dark-mode'
  };
};

export default useTheme;